import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { ArrowDownLeft, ArrowUpRight, CalendarClock, CreditCard, ExternalLink, Landmark, TrendingUp } from 'lucide-react';
import CurrencyService from '@/services/CurrencyService';
import { totalsByCurrency, totalsLabel, euros, bankPayoutStatus } from '@/utils/walletTotals';
import { BACKEND_URL } from '@/config';
import { StatusPill } from '@/components/design';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const SPENT = ['IN_ESCROW', 'IN_TRANSIT', 'DELIVERED', 'COMPLETED', 'DISPUTED'];

export default function WalletPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [missions, setMissions] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const isSender  = user?.roles?.includes('ROLE_SENDER');
  const isCarrier = user?.roles?.includes('ROLE_CARRIER');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/missions?limit=50`).then((r) => setMissions(r.data.data || [])),
      // Montants réels du transporteur, en euros : versements des missions et virements vers sa banque (B11, étape 4)
      isCarrier ? axios.get(`${API}/payouts/wallet`).then((r) => setWallet(r.data)).catch(() => setWallet(null)) : null,
    ]).finally(() => setLoading(false));
  }, [isCarrier]);

  const asShipper = missions.filter((m) => m.shipper_id === user?.id);
  // Par devise, jamais additionnées entre elles (D26) ; l'expéditeur a payé prix + frais Colis2Ship (C8), le prix seul
  // pour une mission d'avant les frais
  const paid     = (m) => m.total_charged ?? m.contracted_price;
  const spent    = totalsByCurrency(asShipper, (m) => SPENT.includes(m.status), paid);
  const inEscrow = totalsByCurrency(asShipper, (m) => m.status === 'IN_ESCROW', paid);

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payouts/account/dashboard`);
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir votre tableau de bord Stripe.');
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="c2s-topbar">
        <div className="c2s-topbar-title">Portefeuille</div>
        <div className="c2s-topbar-sub">Vue financière de votre activité sur Colis2Ship</div>
      </div>

      {loading ? (
        <div className="kpi-grid">
          {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ height: 110 }} />)}
        </div>
      ) : (
        <>
          <div className="kpi-grid">
            {isSender && (
              <>
                <div className="kpi-card kpi-navy">
                  <div className="kpi-icon"><ArrowUpRight size={18} color="var(--navy)" /></div>
                  <div className="kpi-label">Total dépensé</div>
                  <div className="kpi-value" data-testid="wallet-spent">{totalsLabel(spent)}</div>
                  <div className="kpi-sub">Colis envoyés, dans la devise de chaque mission</div>
                </div>
                <div className="kpi-card kpi-gold">
                  <div className="kpi-icon"><CreditCard size={18} color="var(--gold)" /></div>
                  <div className="kpi-label">En séquestre</div>
                  <div className="kpi-value" data-testid="wallet-escrow">{totalsLabel(inEscrow)}</div>
                  <div className="kpi-sub">En attente de livraison</div>
                </div>
              </>
            )}
            {isCarrier && wallet && (
              <>
                <div className="kpi-card kpi-teal">
                  <div className="kpi-icon"><ArrowDownLeft size={18} color="var(--teal)" /></div>
                  <div className="kpi-label">Versés</div>
                  <div className="kpi-value" data-testid="wallet-earned">{euros(wallet.sent_eur)}</div>
                  <div className="kpi-sub">Sur votre compte de versement</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-icon"><TrendingUp size={18} color="var(--ink3)" /></div>
                  <div className="kpi-label">À venir</div>
                  <div className="kpi-value" data-testid="wallet-upcoming">{euros(wallet.upcoming_eur)}</div>
                  <div className="kpi-sub">48 h après chaque livraison confirmée</div>
                </div>
              </>
            )}
          </div>

          {isCarrier && wallet && wallet.waiting_account_eur > 0 && (
            <div className="card" style={{ marginBottom: 16 }} data-testid="wallet-waiting-account">
              <strong>{euros(wallet.waiting_account_eur)} vous attendent.</strong>{' '}
              Configurez vos versements pour les recevoir. <a href="/app/versements">Configurer mes versements</a>
            </div>
          )}

          {isCarrier && wallet && (
            <div className="card" style={{ marginBottom: 16 }} data-testid="wallet-bank-payouts">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Landmark size={16} aria-hidden="true" /> Virements vers votre banque
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarClock size={14} aria-hidden="true" />
                    Un virement chaque lundi, regroupant les versements disponibles (Stripe les rend disponibles 7 jours après leur arrivée).
                  </div>
                </div>
                {wallet.account_status === 'READY' && (
                  <button type="button" className="btn btn-sm" onClick={openDashboard} disabled={busy} data-testid="wallet-stripe-dashboard">
                    <ExternalLink size={14} aria-hidden="true" /> Mon tableau de bord Stripe
                  </button>
                )}
              </div>
              {wallet.bank_payouts.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--ink3)', marginTop: 12 }} data-testid="wallet-bank-payouts-empty">
                  {wallet.account_status === 'READY'
                    ? 'Aucun virement vers votre banque pour l\'instant.'
                    : <>Configurez vos versements pour recevoir vos gains. <a href="/app/versements">Mes versements</a></>}
                </div>
              ) : (
                <table className="c2s-table" style={{ marginTop: 12 }}>
                  <thead><tr><th>Arrivée prévue</th><th>État</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
                  <tbody>
                    {wallet.bank_payouts.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontSize: 13 }}>{formatDate(p.arrival_date)}</td>
                        <td style={{ fontSize: 13 }}>{bankPayoutStatus(p.status)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{CurrencyService.formatAmount(p.amount, p.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="card">
            <div className="section-title">Historique des transactions</div>
            {missions.filter((m) => m.contracted_price > 0).length === 0 ? (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div>Aucune transaction pour l'instant.</div>
              </div>
            ) : (
              <table className="c2s-table">
                <thead>
                  <tr>
                    <th>Mission</th>
                    <th>Trajet</th>
                    <th>Rôle</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th style={{ textAlign: 'right' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {missions.filter((m) => m.contracted_price > 0).map((m) => {
                    const isMyShipment = m.shipper_id === user?.id;
                    return (
                      <tr key={m.id}>
                        <td style={{ fontSize: 13 }}>#{m.id.slice(-6)}</td>
                        <td style={{ fontSize: 13 }}>{m.shipment?.departure_city || '?'} → {m.shipment?.arrival_city || '?'}</td>
                        <td><span className={`badge ${isMyShipment ? 'badge-colis' : 'badge-trajet'}`}>{isMyShipment ? 'Expéditeur' : 'Transporteur'}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--ink2)' }}>{formatDate(m.created_at)}</td>
                        <td>
                          <StatusPill domain="mission" code={m.status} />
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--f-display)', fontWeight: 700, color: isMyShipment ? 'var(--red)' : 'var(--teal)' }}
                          data-testid={`wallet-amount-${m.id}`}>
                          {isMyShipment ? '-' : '+'}{CurrencyService.formatAmount(isMyShipment ? paid(m) : m.contracted_price, m.currency || 'EUR')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
